import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import { API_URL } from "../../utils/constant";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Dropdown, Form, Button } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { toastr } from "react-redux-toastr";
import RiskKanbanView from './RiskKanbanView';
import Pagination from "../Pagination/Pagination";
import RiskAssignmentService from '../../services/riskAssignmentService';
import $ from 'jquery';
import 'jquery-confirm';
// Material-UI imports for report dropdown
import { Box, FormControl, Select as MuiSelect, MenuItem } from '@mui/material';

const snoozeOptions = [
    { value: 'falsePositive', label: 'False Positive', class: 'info', icon: 'fa-ban' },
    { value: 'riskAccepted', label: 'Risk Accepted', class: 'warning', icon: 'fa-check-circle' },
    { value: 'remediated', label: 'Remediated', class: 'success', icon: 'fa-shield-alt' }
];

// Utility function to check if value should be hidden (N/A, null, empty, etc.)
const shouldHideValue = (value) => {
    if (!value) return true;
    if (typeof value === 'string') {
        const cleanValue = value.trim().toLowerCase();
        return cleanValue === 'n/a' || cleanValue === 'na' || cleanValue === '' ||
            cleanValue === 'null' || cleanValue === 'undefined' || cleanValue === 'unknown';
    }
    return false;
};

// Utility function to detect URLs and make them clickable
const makeUrlsClickable = (text) => {
    if (!text || typeof text !== 'string') return text;

    // URL regex pattern that matches http/https URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    const parts = text.split(urlRegex);
    return parts.map((part, index) => {
        if (urlRegex.test(part)) {
            return (
                <a
                    key={index}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary"
                    style={{ wordBreak: 'break-all' }}
                >
                    {part}
                </a>
            );
        }
        return part;
    });
};

// Utility function to parse and conditionally render subtitle with score and IP
const renderSubtitleWithScoreCheck = (subtitle) => {
    if (!subtitle || typeof subtitle !== 'string') return subtitle;

    // Hide if subtitle is N/A or empty
    if (shouldHideValue(subtitle)) return null;

    // Check if subtitle contains score and IP pattern
    const scoreIPPattern = /Score:\s*(\w+)(?:,\s*IP:\s*([^,\s]+))?/;
    const match = subtitle.match(scoreIPPattern);

    if (match) {
        const score = match[1];
        const ip = match[2];

        // Hide both score and IP if score is null, undefined, N/A, or 'null' string
        if (!score || score === 'null' || score === 'undefined' || shouldHideValue(score)) {
            // Return the subtitle without the score and IP part
            const remainingText = subtitle.replace(scoreIPPattern, '').replace(/^,\s*/, '').trim();
            return remainingText && !shouldHideValue(remainingText) ? makeUrlsClickable(remainingText) : null;
        }

        // If score exists and is valid, show both score and IP
        return makeUrlsClickable(subtitle);
    }

    // If no score/IP pattern found, return original subtitle
    return makeUrlsClickable(subtitle);
};

/**
 * ConsolidatedRisks Component
 * 
 * Category Filter Implementation:
 * - Supports multiple category selection via checkboxes
 * - Categories are stored as an array in filters.category
 * - When categories are selected, they are sent to the API as separate query parameters
 * - Example: ?category=activeVulnerabilities&category=applicationSecurity
 * - When no categories are selected, the category parameter is removed to show all categories
 * - The filter state is properly managed with React hooks and useCallback optimization
 */
const ConsolidatedRisks = () => {
    // Add CSS to hide Bootstrap dropdown's default ::after pseudo-element and improve table responsiveness
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .dropdown-toggle::after {
                display: none !important;
            }
            .min-width-0 {
                min-width: 0;
            }
            .text-truncate {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .table-responsive {
                -webkit-overflow-scrolling: touch;
            }
            .bg-info-light {
                background-color: rgba(23, 162, 184, 0.1) !important;
            }
            .bg-success-light {
                background-color: rgba(40, 167, 69, 0.1) !important;
            }
            .bg-warning-light {
                background-color: rgba(255, 193, 7, 0.1) !important;
            }
            .fa-bug:before {
                font-size: 14px;
            }
            @media (max-width: 768px) {
                .table-responsive {
                    font-size: 0.875rem;
                }
                .btn-sm {
                    font-size: 0.75rem !important;
                    padding: 0.25rem 0.5rem !important;
                }
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);
    const [risks, setRisks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        severity: '',
        category: [],
        assignedTo: [],
        assignmentStatus: '',
        from: '',
        to: '',
        search: ''
    });
    const [sorting, setSorting] = useState({
        sortBy: 'createdAt',
        sortOrder: -1  // Start with descending order (newest first)
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 20
    });

    // Debug pagination state changes
    useEffect(() => {
        // console.log removed
    }, [pagination]);

    // Debug filter state changes
    useEffect(() => {
        // console.log removed
    }, [filters]);
    const [selectedRisk, setSelectedRisk] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [selectedRisks, setSelectedRisks] = useState([]);
    const [showActionModal, setShowActionModal] = useState(false);
    const [actionType, setActionType] = useState('');
    const [actionNote, setActionNote] = useState('');
    const [actionFor, setActionFor] = useState('forever');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [siemSubmittingId, setSiemSubmittingId] = useState(null);
    // Report dropdown state
    const [selectedValue, setSelectedValue] = useState('');
    const [downloadingReport, setDownloadingReport] = useState(false);
    const [showFreshdeskModal, setShowFreshdeskModal] = useState(false);
    const [submittingFreshdesk, setSubmittingFreshdesk] = useState(false);
    const [selectedTicketRisk, setSelectedTicketRisk] = useState(null);
    const [freshdeskForm, setFreshdeskForm] = useState({
        email: '',
        description: '',
        priority: 0,
        status: 0,
        tags: [],
        assetId: '',
        assetType: '',
        title: '',
        organisationId: ''
    });

    // API request tracking to prevent duplicate calls
    const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);
    const apiCallQueueRef = useRef(null);
    const currentRequestRef = useRef(null);
    const lastRequestParamsRef = useRef(null);
    const refreshLockRef = useRef(false);

    // Add ref to track if component is mounted
    const isMountedRef = useRef(true);

    // Add missing state variables for tab navigation
    const [category, setCategory] = useState("all");
    const [categoryV, setCategoryV] = useState("all"); // Sent to API via path segment (not query param)
    const [page, setPage] = useState(1);
    const [filterData, setfilterData] = useState("all");
    const [searchBy, setSearchBy] = useState("subDomainName"); // UI-only, not sent to API
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);

    // Timer to debounce filter-triggered fetches
    const filtersFetchTimerRef = useRef(null);
    const { orgId } = useParams();
    const id = useSelector(state => {
        if (orgId) {
            return orgId;
        } else {
            return state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
        }
    });

    // Debug logging to help identify when id becomes available
    useEffect(() => {
        // console.log removed
    }, [id]);

    useEffect(() => {
        if (id) {
            setFreshdeskForm(prev => ({
                ...prev,
                organisationId: id
            }));
        }
    }, [id]);

    // Cleanup effect to prevent memory leaks
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            // Clear any pending timers on cleanup
            if (filtersFetchTimerRef.current) {
                clearTimeout(filtersFetchTimerRef.current);
            }
            if (apiCallQueueRef.current) {
                clearTimeout(apiCallQueueRef.current);
            }
            // Cancel any pending requests
            currentRequestRef.current = null;
            lastRequestParamsRef.current = null;
            refreshLockRef.current = false;
        };
    }, []);

    // Sync category state with filters and trigger API call
    useEffect(() => {
        if (id) {
            // Update filters.category based on the selected category tab
            if (category === "all") {
                setFilters(prev => ({ ...prev, category: [] }));
            } else {
                setFilters(prev => ({ ...prev, category: [category] }));
            }
        }
    }, [category, id]);





    // Sync search and other filter states
    useEffect(() => {
        if (id) {
            setFilters(prev => ({
                ...prev,
                search: search || ''
            }));
        }
    }, [search, id]);

    const categories = [
        { value: 'activeVulnerabilities', label: 'Active Vulnerabilities' },
        { value: 'applicationSecurity', label: 'Application Security' },
        { value: 'passiveVulnerabilities', label: 'Passive Vulnerability Assessment' },
        { value: 'vulnerableTechnologies', label: 'Vulnerable Web Technologies' },
        { value: 'emailSecurity', label: 'Email Security - SPF/DMARC Records' },
        { value: 'sslCertificates', label: 'Expired & Vulnerable SSL Certificates' },
        { value: 'leakedCredentials', label: 'Breached Credentials' },
        { value: 'malwareCredentials', label: 'Malware Infected Computers' },
        { value: 'leakedSessions', label: 'Leaked Sessions' },
        { value: 'leakedCode', label: 'Source Code Leakage' },
        { value: 'pastebinLeaks', label: 'Pastebin Monitoring' },
        { value: 'personalInfo', label: 'Personal Information Leakage' },
        { value: 'telegramMentions', label: 'Telegram Monitoring' },
        { value: 'postmanExposure', label: 'API Documentation Leak Monitoring' },
        { value: 'cloudBuckets', label: 'Open Cloud Buckets' },
        { value: 'darkWebForums', label: 'Dark Web Monitoring' },
        { value: 'lookAlikeDomains', label: 'Look Alike Domain' },
        { value: 'mobileApps', label: 'Fake Mobile Applications' },
        { value: 'socialMedia', label: 'Fake Social Media Profiles' },
        { value: 'brandMentions', label: 'Brand Mentions' }
    ];

    const severityLevels = [
        { value: 'Critical', label: 'Critical', class: 'danger' },
        { value: 'High', label: 'High', class: 'danger' },
        { value: 'Medium', label: 'Medium', class: 'warning' },
        { value: 'Low', label: 'Low', class: 'info' }
    ];

    const assignmentStatuses = [
        { value: 'pending', label: 'Pending', class: 'warning' },
        { value: 'in-progress', label: 'In Progress', class: 'primary' },
        { value: 'completed', label: 'Completed', class: 'success' },
        { value: 'rejected', label: 'Rejected', class: 'danger' }
    ];

    // Initialize service
    const riskService = new RiskAssignmentService();

    // Fetch users for assignment
    const fetchUsers = async () => {
        if (!id) return;

        try {
            setLoadingUsers(true);
            // console.log removed
            const response = await riskService.getUsers(id);
            // console.log removed
            setUsers(response.data || response || []);
        } catch (error) {
            // console.error removed
            // console.error removed
            riskService.handleApiError(error);
        } finally {
            setLoadingUsers(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchUsers();
        }
    }, [id]);

    const fetchRisks = useCallback(async (page = 1, limitOverride = null) => {
        // Guard clause to prevent API call when id is undefined
        if (!id) {
            // console.warn removed
            return;
        }

        // Create request signature for deduplication
        const requestSignature = JSON.stringify({ page, limitOverride, filters, sorting, categoryV });

        // Prevent duplicate API calls with same parameters
        if (isApiCallInProgress && lastRequestParamsRef.current === requestSignature) {
            console.log('Duplicate API call detected, skipping:', requestSignature);
            return;
        }

        // Cancel any existing request
        if (currentRequestRef.current) {
            console.log('Cancelling previous API request');
            currentRequestRef.current = null;
        }

        console.log(`Starting API call - fetchRisks(page: ${page})`);
        lastRequestParamsRef.current = requestSignature;
        const requestId = Date.now();
        currentRequestRef.current = requestId;

        try {
            setIsApiCallInProgress(true);
            setLoading(true);
            const params = {
                page: page || 1,
                limit: limitOverride !== null ? limitOverride : pagination.limit,
                sortBy: sorting.sortBy,
                sortOrder: sorting.sortOrder,
                categoryV, // used by service to build path; service will not include in query params
                ...filters
            };

            // Handle category array for multiple category selection
            if (filters.category && filters.category.length > 0) {
                // Send category as array for multiple categories
                params.category = filters.category;
                // console.log removed
                // console.log removed
            } else {
                // Remove category parameter when empty to show all categories
                delete params.category;
                // console.log removed
            }

            // Build params complete

            const response = await riskService.getRisks(id, params);

            // Check if this request was cancelled
            if (currentRequestRef.current !== requestId) {
                console.log('Request was cancelled, ignoring response');
                return;
            }

            // Debug pagination response removed

            // Ensure we're setting the correct data
            const risksData = response.data || [];

            // Check if backend is returning paginated data or all data
            const expectedPageSize = pagination.limit;
            const actualDataSize = risksData.length;

            // console.log removed

            // If backend returns more data than expected, it might not be paginating properly
            if (actualDataSize > expectedPageSize && !response.pagination) {
                //

                // Client-side pagination fallback
                const startIndex = (page - 1) * expectedPageSize;
                const endIndex = startIndex + expectedPageSize;
                const paginatedData = risksData.slice(startIndex, endIndex);

                if (isMountedRef.current) {
                    setRisks(paginatedData);
                    setPagination(prev => ({
                        ...prev,
                        currentPage: page,
                        totalPages: Math.ceil(actualDataSize / prev.limit),
                        totalRecords: actualDataSize
                    }));
                }
            } else {
                // Backend is handling pagination correctly
                if (isMountedRef.current) {
                    setRisks(risksData);

                    // Update pagination state with response data
                    if (response.pagination) {
                        setPagination(prev => ({
                            ...prev,
                            currentPage: response.pagination.page || page,
                            totalPages: response.pagination.pages || 1,
                            totalRecords: response.pagination.total || risksData.length
                        }));
                    } else {
                        // Fallback pagination if backend doesn't provide pagination info
                        setPagination(prev => ({
                            ...prev,
                            currentPage: page,
                            totalPages: Math.ceil((risksData.length || 0) / prev.limit),
                            totalRecords: risksData.length || 0
                        }));
                    }
                }
            }
        } catch (error) {
            // console.error removed
            riskService.handleApiError(error);
        } finally {
            if (isMountedRef.current && currentRequestRef.current === requestId) {
                setLoading(false);
                setIsApiCallInProgress(false);
                currentRequestRef.current = null;
                lastRequestParamsRef.current = null;
            }
        }
    }, [id, filters, sorting.sortBy, sorting.sortOrder, categoryV]);

    // Handle filter/category changes with debouncing
    useEffect(() => {
        if (!id || !isMountedRef.current) return;

        // Clear any existing timer to prevent multiple calls
        if (filtersFetchTimerRef.current) {
            clearTimeout(filtersFetchTimerRef.current);
        }

        filtersFetchTimerRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;
            setPagination(prev => ({ ...prev, currentPage: 1 }));
            fetchRisks(1);
        }, 300);

        return () => {
            if (filtersFetchTimerRef.current) {
                clearTimeout(filtersFetchTimerRef.current);
            }
        };
    }, [filters, id, categoryV, fetchRisks]);

    // Handle refresh trigger separately with lock mechanism
    useEffect(() => {
        if (refreshTrigger > 0 && id && isMountedRef.current && !refreshLockRef.current) {
            console.log('Force refreshing data after successful action...');
            refreshLockRef.current = true;

            // Clear any existing timer
            if (apiCallQueueRef.current) {
                clearTimeout(apiCallQueueRef.current);
            }

            apiCallQueueRef.current = setTimeout(() => {
                if (!isMountedRef.current) {
                    refreshLockRef.current = false;
                    return;
                }

                fetchRisks(pagination.currentPage).then(() => {
                    console.log('Data refresh completed');
                    // Reset refresh trigger and lock after successful refresh
                    if (isMountedRef.current) {
                        setRefreshTrigger(0);
                        refreshLockRef.current = false;
                    }
                }).catch((error) => {
                    console.error('Data refresh failed:', error);
                    toastr.error('Error', 'Failed to reload data after action. Please refresh the page.');
                    // Reset refresh trigger and lock even on error
                    if (isMountedRef.current) {
                        setRefreshTrigger(0);
                        refreshLockRef.current = false;
                    }
                });
            }, 300);
        }

        return () => {
            if (apiCallQueueRef.current) {
                clearTimeout(apiCallQueueRef.current);
                apiCallQueueRef.current = null;
            }
        };
    }, [refreshTrigger, id, pagination.currentPage, fetchRisks]);

    // We're handling pagination.limit changes directly in the dropdown onChange handler
    // No need for a separate useEffect to avoid duplicate API calls

    // Category tab changes are handled by the debounced filters effect above

    const handlePageChange = useCallback((page) => {
        if (page !== pagination.currentPage && !isApiCallInProgress && isMountedRef.current) {
            console.log(`Page change requested: ${page}`);
            fetchRisks(page);
        }
    }, [pagination.currentPage, isApiCallInProgress, fetchRisks]);

    const handleFilterChange = (field, value) => {
        if (isMountedRef.current) {
            setFilters(prev => ({
                ...prev,
                [field]: value
            }));

            // Clear selected records when filters change
            setSelectedRisks([]);

            // For severity filter especially, make sure we're logging the value being set
            if (field === 'severity') {
                console.log(`Setting severity filter to: ${value}`);
            }

            // Clear search when other filters change for better UX
            if (field !== 'search' && searchInput) {
                setSearchInput("");
                setSearch("");
            }

            // Reset to page 1 - API call will be triggered by useEffect watching filters
            setPagination(prev => ({ ...prev, currentPage: 1 }));
        }
    };

    const handleCategoryFilterChange = (categoryValue, isChecked) => {
        if (isMountedRef.current) {
            setFilters(prev => ({
                ...prev,
                category: isChecked
                    ? [...prev.category, categoryValue]
                    : prev.category.filter(cat => cat !== categoryValue)
            }));
            // Clear selected records when category filters change
            setSelectedRisks([]);
        }
    };

    const handleClearAllCategories = () => {
        if (isMountedRef.current) {
            setFilters(prev => ({
                ...prev,
                category: []
            }));
            // Clear selected records when clearing category filters
            setSelectedRisks([]);
            // Reset to page 1 - API call will be triggered by useEffect
            setPagination(prev => ({ ...prev, currentPage: 1 }));
        }
    };

    const handleClearAllFilters = () => {
        if (isMountedRef.current) {
            setFilters({
                severity: '',
                category: [],
                assignedTo: [],
                assignmentStatus: '',
                from: '',
                to: '',
                search: ''
            });

            // Clear selected records when clearing all filters
            setSelectedRisks([]);

            // Reset pagination to page 1 and limit to 20 items - API call will be triggered by useEffect
            setPagination(prev => ({ ...prev, currentPage: 1, limit: 20 }));
        }
    };

    const handleSortChange = (field) => {
        if (isMountedRef.current) {
            // First update the sorting state - when clicking on a column:
            // 1. If it's a new column, start with ascending order (1)
            // 2. If it's the same column, toggle between ascending (1) and descending (-1)
            setSorting(prev => {
                const newOrder = prev.sortBy === field ?
                    (prev.sortOrder === 1 ? -1 : 1) :
                    1; // Start with ascending order on first click

                return {
                    sortBy: field,
                    sortOrder: newOrder
                };
            });

            // Then reset pagination
            setPagination(prevPag => ({ ...prevPag, currentPage: 1 }));

            // Note: fetchRisks will be called automatically by the useEffect 
            // that watches for sorting changes, preventing duplicate API calls
        }
    };

    const renderSortIcon = (field) => {
        if (sorting.sortBy !== field) return <i className="fa fa-sort text-muted ml-1"></i>;
        return sorting.sortOrder === 1
            ? <i className="fa fa-sort-up ml-1" style={{ color: '#453acd' }}></i>  // Highlight active sort
            : <i className="fa fa-sort-down ml-1" style={{ color: '#453acd' }}></i>;  // Highlight active sort
    };

    const handleViewDetails = (risk) => {
        setSelectedRisk(risk);
        setShowDetailModal(true);
    };



    // Enhanced assignment functions
    const handleAssignRisk = (risk) => {
        // console.log removed
        setSelectedRisk(risk);
        setShowAssignmentModal(true);
    };

    const handleAssignmentSubmit = async (assignmentData) => {
        try {
            // Check if this is an update to existing assignment or new assignment
            if (selectedRisk.assignment && selectedRisk.assignment._id) {
                // Update existing assignment
                // console.log removed
                await riskService.updateAssignment(selectedRisk.assignment._id, {
                    assignedTo: assignmentData.assignedTo,
                    notes: assignmentData.notes,
                    dueDate: assignmentData.dueDate,
                    status: assignmentData.status
                });
            } else {
                // Create new assignment
                const assignmentPayload = riskService.createAssignmentData(
                    selectedRisk._id,
                    selectedRisk.category,
                    assignmentData.assignedTo,
                    assignmentData.notes,
                    assignmentData.dueDate,
                    assignmentData.status
                );
                // console.log removed
                await riskService.assignRisk(id, assignmentPayload);
            }

            setShowAssignmentModal(false);

            // Force refresh data after assignment
            setTimeout(() => {
                if (isMountedRef.current && !isApiCallInProgress && !refreshLockRef.current) {
                    setRefreshTrigger(prev => prev + 1);
                } else if (refreshLockRef.current) {
                    console.log('Refresh already in progress, skipping assignment refresh');
                }
            }, 300);
        } catch (error) {
            // console.error removed
            riskService.handleApiError(error);
        }
    };

    const handleUpdateAssignment = async (assignmentId, updateData) => {
        try {
            await riskService.updateAssignment(assignmentId, updateData);

            // Force refresh data after assignment update
            setTimeout(() => {
                if (isMountedRef.current && !isApiCallInProgress && !refreshLockRef.current) {
                    setRefreshTrigger(prev => prev + 1);
                } else if (refreshLockRef.current) {
                    console.log('Refresh already in progress, skipping assignment update refresh');
                }
            }, 300);
        } catch (error) {
            // console.error removed
            riskService.handleApiError(error);
        }
    };

    const handleRemoveAssignment = async (assignmentId) => {
        if (window.confirm('Are you sure you want to remove this assignment?')) {
            try {
                await riskService.removeAssignment(assignmentId);
                toastr.success('Success', 'Assignment removed successfully');

                // Force refresh data after assignment removal
                setTimeout(() => {
                    if (isMountedRef.current && !isApiCallInProgress && !refreshLockRef.current) {
                        setRefreshTrigger(prev => prev + 1);
                    } else if (refreshLockRef.current) {
                        console.log('Refresh already in progress, skipping assignment removal refresh');
                    }
                }, 300);
            } catch (error) {
                // console.error removed
                riskService.handleApiError(error);
            }
        }
    };

    const openFreshdeskModal = (risk) => {
        setSelectedTicketRisk(risk);
        setFreshdeskForm(prev => ({
            ...prev,
            assetId: risk?._id || '',
            assetType: risk?.category || prev.assetType,
            title: risk?.displayDetails?.title || prev.title,
            description: prev.description || (risk?.displayDetails?.title ? `Risk: ${risk.displayDetails.title}` : ''),
            organisationId: id
        }));
        setShowFreshdeskModal(true);
    };

    const handleFreshdeskFieldChange = (field, value) => {
        setFreshdeskForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const closeFreshdeskModal = () => {
        setShowFreshdeskModal(false);
        setSelectedTicketRisk(null);
        setFreshdeskForm(prev => ({
            ...prev,
            description: '',
            tags: [],
            assetId: '',
            assetType: '',
            title: ''
        }));
    };

    const submitFreshdeskTicket = async () => {
        if (!freshdeskForm.email || !freshdeskForm.description || !freshdeskForm.title) {
            toastr.error('Error', 'Email, title and description are required');
            return;
        }

        const tagsArray = Array.isArray(freshdeskForm.tags)
            ? freshdeskForm.tags
            : freshdeskForm.tags
                ? freshdeskForm.tags.split(',').map(tag => tag.trim()).filter(Boolean)
                : [];

        const payload = {
            ...freshdeskForm,
            priority: Number(freshdeskForm.priority) || 0,
            status: Number(freshdeskForm.status) || 0,
            tags: tagsArray
        };

        setSubmittingFreshdesk(true);
        try {
            const response = await axios.post(`${API_URL}/freshdesk/tickets`, payload);
            toastr.success('Success', response?.data?.message || 'Ticket created successfully');
            closeFreshdeskModal();
        } catch (error) {
            toastr.error('Error', error?.response?.data?.message || 'Failed to create ticket');
        } finally {
            setSubmittingFreshdesk(false);
        }
    };

    // Report download function with filter and selection support
    const downloadConsolidatedReport = async (selectedOnly = false) => {
        try {
            setDownloadingReport(true);

            if (!id) {
                toastr.error('Error', 'Organisation ID not found');
                return;
            }

            // Check if trying to download selected risks but none are selected
            if (selectedOnly && selectedRisks.length === 0) {
                toastr.warning('Warning', 'Please select at least one risk to download');
                return;
            }

            // Prepare URL and parameters for the API call
            let endpoint = `${API_URL}/organisations/${id}/consolidated-report`;

            // Check if we're requesting specific findings or a time-filtered report
            if (selectedOnly && selectedRisks.length > 0) {
                // For selected risks, group them by category
                const risksById = {};

                // Group selected risks by their category
                selectedRisks.forEach(riskId => {
                    const risk = risks.find(r => r._id === riskId);
                    if (risk) {
                        if (!risksById[risk.category]) {
                            risksById[risk.category] = [];
                        }
                        risksById[risk.category].push(riskId);
                    }
                });

                // Create the findings array format required by the API
                const findings = Object.keys(risksById).map(category => ({
                    category,
                    ids: risksById[category]
                }));

                // If we have findings, use the custom-findings endpoint
                if (findings.length > 0) {
                    endpoint += '/custom-findings';
                    const params = new URLSearchParams();
                    params.append('findings', JSON.stringify(findings));
                    endpoint += `?${params.toString()}`;
                }
            } else {
                // For time-based filtering
                endpoint += '/pdf';
                const params = new URLSearchParams();

                // Add date filters if present
                if (filters.from) {
                    params.append('fromDate', filters.from);
                }

                if (filters.to) {
                    params.append('toDate', filters.to);
                }

                // Add categories if selected
                if (filters.category && filters.category.length > 0) {
                    filters.category.forEach(cat => {
                        params.append('category', cat);
                    });
                }

                if (params.toString()) {
                    endpoint += `?${params.toString()}`;
                }
            }

            // Make the API call
            const response = await axios.get(endpoint, {
                responseType: 'blob'
            });

            // Create blob link to download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const href = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = href;

            // Generate the filename
            const fileName = selectedOnly ?
                `selected-risks-report-${new Date().toISOString().slice(0, 10)}.pdf` :
                `consolidated-risks-report-${new Date().toISOString().slice(0, 10)}.pdf`;

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
            toastr.success('Success', `${selectedOnly ? 'Selected' : 'All'} risks report downloaded successfully`);
        } catch (error) {
            console.error('Download error:', error);
            toastr.error('Error', 'Failed to download risks report. Please try again.');
        } finally {
            setDownloadingReport(false);
        }
    };

    // Risk action functions
    const handleRiskAction = async (riskIds, action, note = '', actionFor = 'forever') => {
        try {
            // Get the category from the selected risk or from the first selected risk
            let category = '';
            if (selectedRisk) {
                category = selectedRisk.category;
            } else if (riskIds.length > 0) {
                // Find the category from the first selected risk in the current risks list
                const firstRisk = risks.find(risk => risk._id === riskIds[0]);
                category = firstRisk?.category || '';
            }

            // Map category to the correct parameter name expected by backend
            const getCategoryParamName = (category) => {
                const categoryParamMap = {
                    'activeVulnerabilities': 'pvaIds',
                    'passiveVulnerabilities': 'pvaIds',
                    'applicationSecurity': 'ids',
                    'vulnerableTechnologies': 'techIds',
                    'emailSecurity': 'spfDmarcIds',
                    'sslCertificates': 'subDomainIds',
                    'leakedCredentials': 'leakedCredentialsIds',
                    'malwareCredentials': 'malwareCredentialsIds',
                    'leakedSessions': 'leakedSessionIds',
                    'leakedCode': 'leakedCodeIds',
                    'pastebinLeaks': 'pastebinIds',
                    'personalInfo': 'piiIds',
                    'telegramMentions': 'telegramIds',
                    'postmanExposure': 'postmanIds',
                    'cloudBuckets': 'cloudBucketIds',
                    'darkWebForums': 'darkWebForumIds',
                    'lookAlikeDomains': 'lookalikeDomainIds',
                    'mobileApps': 'lookalikeAppIds',
                    'socialMedia': 'fakeSocialMediaIds',
                    'brandMentions': 'brandMentionIds'
                };
                return categoryParamMap[category] || 'riskIds';
            };

            const categoryParamName = getCategoryParamName(category);

            // console.log removed

            const requestData = {
                action,
                category,
                riskIds: Array.isArray(riskIds) ? riskIds : [riskIds],
                [categoryParamName]: Array.isArray(riskIds) ? riskIds : [riskIds],
                actionNote: note,
                actionFor
            };

            // console.log removed

            const response = await riskService.performRiskAction(id, requestData);

            if (response.success) {
                toastr.success('Success', response.userMessage || 'Action completed successfully');

                // Clear selections and close modal immediately
                setSelectedRisks([]);
                setShowActionModal(false);
                setActionType('');
                setActionNote('');
                setActionFor('forever');

                // Force refresh data after a short delay to ensure backend has processed the action
                setTimeout(() => {
                    if (isMountedRef.current && !isApiCallInProgress && !refreshLockRef.current) {
                        console.log('Triggering refresh after successful action');
                        setRefreshTrigger(prev => prev + 1);
                    } else if (refreshLockRef.current) {
                        console.log('Refresh already in progress, skipping duplicate trigger');
                    }
                }, 500);
            } else {
                toastr.error('Error', response.userMessage || 'Failed to perform action');
            }
        } catch (error) {
            // console.error removed
            toastr.error('Error', error.response?.data?.userMessage || 'Failed to perform action');
        }
    };

    const handleSingleRiskAction = (risk, action) => {
        setSelectedRisk(risk);
        setActionType(action);
        setShowActionModal(true);
    };

    const handleRaiseSiemAlert = (risk) => {
        const riskIdentifier = risk?._id;

        if (!riskIdentifier || !risk?.category || !id) {
            toastr.error('Error', 'Missing details to raise SIEM alert');
            return;
        }

        $.confirm({
            title: 'Raise SIEM alert?',
            content: 'This will raise a SIEM alert for the selected risk. Continue?',
            type: 'success',
            buttons: {
                confirm: {
                    text: 'Yes, raise alert',
                    btnClass: 'btn-success',
                    action: async () => {
                        const payload = {
                            moduleName: risk.category,
                            id: riskIdentifier
                        };

                        setSiemSubmittingId(riskIdentifier);

                        try {
                            await axios.post(`${API_URL}/organisations/${id}/siem/alert`, payload);
                            toastr.success('Success', 'SIEM alert raised successfully');
                        } catch (error) {
                            const message = error?.response?.data?.userMessage || 'Failed to raise SIEM alert';
                            toastr.error('Error', message);
                        } finally {
                            setSiemSubmittingId(null);
                        }
                    }
                },
                cancel: {
                    text: 'Cancel',
                    btnClass: 'btn-secondary'
                }
            }
        });
    };

    const handleBulkAction = () => {
        if (selectedRisks.length === 0) {
            toastr.warning('Warning', 'Please select at least one risk');
            return;
        }
        setShowActionModal(true);
    };

    const handleRiskSelection = (riskId) => {
        setSelectedRisks(prev =>
            prev.includes(riskId)
                ? prev.filter(id => id !== riskId)
                : [...prev, riskId]
        );
    };

    const handleSelectAll = () => {
        if (selectedRisks.length === risks.length) {
            setSelectedRisks([]);
        } else {
            setSelectedRisks(risks.map(risk => risk._id));
        }
    };

    const renderActions = (risk) => (
        <td className="py-3 px-4">
            <div className="d-flex align-items-center flex-wrap">
                <button
                    className="btn btn-sm btn-outline-primary mr-1 mb-1"
                    onClick={() => handleViewDetails(risk)}
                    title="Show Details"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                    <i className="fa fa-eye mr-1"></i>
                    Details
                </button>
                <button
                    className="btn btn-sm btn-outline-info mr-1 mb-1"
                    onClick={() => openFreshdeskModal(risk)}
                    title="Raise Freshdesk Ticket"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                    <i className="fa fa-ticket mr-1"></i>
                    Raise Ticket
                </button>

                <button
                    className="btn btn-sm btn-outline-info mr-1 mb-1"
                    onClick={() => handleRaiseSiemAlert(risk)}
                    disabled={siemSubmittingId === (risk?._id)}
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                    <i className="fa fa-bell mr-2"></i>
                    {siemSubmittingId === (risk?._id) ? 'Raising SIEM alert...' : 'Raise SIEM alert'}
                </button>

                <Dropdown className="mr-1 mb-1">
                    <Dropdown.Toggle variant="outline-info" size="sm" className="px-2" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
                        <i className="fa fa-user mr-1"></i>
                        Assign
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="shadow-sm border-0">
                        <Dropdown.Item
                            onClick={() => handleAssignRisk(risk)}
                            className="d-flex align-items-center py-2"
                        >
                            <i className="fa fa-edit mr-2 text-primary"></i>
                            Edit Assignment
                        </Dropdown.Item>
                        {risk.assignment && Object.keys(risk.assignment).length > 0 && (
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                    onClick={() => handleRemoveAssignment(risk?.assignment?._id)}
                                    className="d-flex align-items-center py-2 text-danger"
                                >
                                    <i className="fa fa-user-times mr-2"></i>
                                    Remove Assignment
                                </Dropdown.Item>
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>

                <Dropdown>
                    <Dropdown.Toggle variant="outline-warning" size="sm" className="px-2" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
                        <i className="fa fa-flag mr-1"></i>
                        Actions
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="shadow-sm border-0">
                        <Dropdown.Item
                            onClick={() => handleSingleRiskAction(risk, 'markAsRemediated')}
                            className="d-flex align-items-center py-2 text-success"
                        >
                            <i className="fa fa-check-circle mr-2"></i>
                            Mark as Remediated
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => handleSingleRiskAction(risk, 'markAsFalsePositive')}
                            className="d-flex align-items-center py-2 text-info"
                        >
                            <i className="fa fa-ban mr-2"></i>
                            Mark as False Positive
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => handleSingleRiskAction(risk, 'riskAccepted')}
                            className="d-flex align-items-center py-2 text-warning"
                        >
                            <i className="fa fa-shield mr-2"></i>
                            Accept Risk
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                            onClick={() => handleSingleRiskAction(risk, 'resetAction')}
                            className="d-flex align-items-center py-2 text-secondary"
                        >
                            <i className="fa fa-undo mr-2"></i>
                            Reset Action
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </td>
    );

    const renderTableRow = (risk) => (
        <tr key={risk._id} className="border-bottom">
            <td className="py-3 px-4">
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedRisks.includes(risk._id)}
                        onChange={() => handleRiskSelection(risk._id)}
                    />
                </div>
            </td>
            <td className="py-3 px-4">
                <div className="d-flex align-items-center">
                    <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mr-3" style={{ width: '32px', height: '32px' }}>
                        <i className="fa fa-tag text-muted" style={{ fontSize: '0.8rem' }}></i>
                    </div>
                    {!shouldHideValue(risk.category) && (
                        <span className="font-weight-medium">
                            {categories.find(c => c.value === risk.category)?.label || risk.category}
                        </span>
                    )}
                </div>
            </td>
            <td className="py-3 px-4">
                <div className="d-flex flex-column">
                    {!shouldHideValue(risk.severity) && (
                        <span className={`badge badge-${severityLevels.find(s => s.value === risk.severity)?.class || 'secondary'} px-3 py-2`}>
                            <i className={`fa fa-circle mr-1 text-${severityLevels.find(s => s.value === risk.severity)?.class || 'secondary'}`}></i>
                            {risk.severity}
                        </span>
                    )}
                </div>
            </td>
            <td className="py-3 px-4" style={{ maxWidth: '350px', wordWrap: 'break-word', wordBreak: 'break-word' }}>
                <div className="d-flex flex-column" style={{ maxWidth: '100%' }}>
                    {!shouldHideValue(risk.displayDetails?.title) && (
                        <>
                            {risk.displayDetails?.title ? (
                                <a
                                    href={risk.displayDetails.title.startsWith('http') ? risk.displayDetails.title : `https://${risk.displayDetails.title}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className="font-weight-medium text-primary text-decoration-none"
                                    style={{
                                        wordWrap: 'break-word',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        lineHeight: '1.4'
                                    }}
                                >
                                    {risk.displayDetails?.title}
                                </a>
                            ) : (
                                <span
                                    className="font-weight-medium text-dark"
                                    style={{
                                        wordWrap: 'break-word',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        lineHeight: '1.4'
                                    }}
                                >
                                    {risk.displayDetails?.title}
                                </span>
                            )}
                        </>
                    )}
                    {risk.displayDetails?.subtitle && renderSubtitleWithScoreCheck(risk.displayDetails.subtitle) && (
                        <small
                            className="text-muted"
                            style={{
                                wordWrap: 'break-word',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal',
                                lineHeight: '1.3'
                            }}
                        >
                            {renderSubtitleWithScoreCheck(risk.displayDetails.subtitle)}
                        </small>
                    )}
                    {risk.category === 'vulnerableTechnologies' && risk.highestExploitability && !shouldHideValue(risk.highestExploitability.toString()) && (
                        <div className="mt-1">
                            <small
                                className="text-muted"
                                style={{
                                    wordWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'normal',
                                    lineHeight: '1.3'
                                }}
                            >
                                <i className="fa fa-bug mr-1"></i>
                                Exploitability: {risk.highestExploitability.toFixed(1)}
                                {risk.highestCvssScore && !shouldHideValue(risk.highestCvssScore.toString()) && (
                                    <span className="ml-2">
                                        <i className="fa fa-shield mr-1"></i>
                                        CVSS: {risk.highestCvssScore.toFixed(1)}
                                    </span>
                                )}
                            </small>
                        </div>
                    )}
                    {risk.category === 'vulnerableTechnologies' && risk.status && !shouldHideValue(risk.status) && (
                        <div className="mt-1">
                            <span
                                className={`badge badge-${risk.status === 'Exploitable' ? 'danger' : 'warning'} px-2 py-1`}
                                style={{ fontSize: '0.7rem', marginLeft: '0px' }}
                                title={risk.status === 'Exploitable' ? 'This technology has known exploits available' : 'This technology has vulnerabilities but no known exploits'}
                            >
                                <i className={`fa fa-${risk.status === 'Exploitable' ? 'exclamation-triangle' : 'shield-alt'} mr-1`}></i>
                                {risk.status}
                            </span>
                        </div>
                    )}
                </div>
            </td>
            <td className="py-3 px-4">
                <div className="d-flex flex-column">
                    <span className="font-weight-medium">
                        {moment(risk.createdAt || risk.scannedAt || risk.updatedAt).format('MMM DD, YYYY')}
                    </span>
                    <small className="text-muted">
                        {moment(risk.createdAt || risk.scannedAt || risk.updatedAt).format('HH:mm')}
                    </small>
                </div>
            </td>

            <td className="py-3 px-4">
                {risk?.assignment && Object.keys(risk.assignment).length > 0 ? (
                    <div className="d-flex flex-column">
                        <div className="d-flex align-items-center">
                            <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center mr-2" style={{ width: '24px', height: '24px' }}>
                                <i className="fa fa-user text-white" style={{ fontSize: '0.6rem' }}></i>
                            </div>
                            <div className="flex-grow-1 min-width-0">
                                <div className="font-weight-medium text-truncate" title={risk.assignment.assignedTo && risk.assignment.assignedTo.length > 0
                                    ? risk.assignment.assignedTo
                                        .map(user => {
                                            const userName = user.fullName || user.name;
                                            return !shouldHideValue(userName) ? userName : null;
                                        })
                                        .filter(name => name !== null)
                                        .join(', ') || 'No user assigned'
                                    : 'No user assigned'}>
                                    {risk.assignment.assignedTo && risk.assignment.assignedTo.length > 0
                                        ? risk.assignment.assignedTo
                                            .map(user => {
                                                const userName = user.fullName || user.name;
                                                return !shouldHideValue(userName) ? userName : null;
                                            })
                                            .filter(name => name !== null)
                                            .join(', ') || 'No user assigned'
                                        : 'No user assigned'
                                    }
                                </div>
                                <div className="d-flex align-items-center mt-1">
                                    {!shouldHideValue(risk.assignment.status) && (
                                        <small className={`badge badge-${assignmentStatuses.find(s => s.value === risk.assignment.status)?.class || 'secondary'} mr-2`} style={{ fontSize: '0.65rem' }}>
                                            {assignmentStatuses.find(s => s.value === risk.assignment.status)?.label || risk.assignment.status || 'Unknown'}
                                        </small>
                                    )}
                                    {risk.assignment.dueDate && (
                                        <small className={`text-${moment(risk.assignment.dueDate).isBefore(moment()) ? 'danger' : 'muted'}`} style={{ fontSize: '0.7rem' }}>
                                            <i className="fa fa-calendar mr-1"></i>
                                            {moment(risk.assignment.dueDate).format('MMM DD')}
                                            {moment(risk.assignment.dueDate).isBefore(moment()) && ' (Overdue)'}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="d-flex align-items-center">
                        <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mr-2" style={{ width: '24px', height: '24px' }}>
                            <i className="fa fa-user-slash text-muted" style={{ fontSize: '0.6rem' }}></i>
                        </div>
                        <span className="text-muted" style={{ fontSize: '0.9rem' }}>Unassigned</span>
                    </div>
                )}
            </td>
            {renderActions(risk)}
        </tr>
    );

    // Assignment Modal Component
    const AssignmentModal = () => {
        const [selectedUsers, setSelectedUsers] = useState([]);
        const [notes, setNotes] = useState('');
        const [dueDate, setDueDate] = useState('');
        const [status, setStatus] = useState('pending');
        const [submitting, setSubmitting] = useState(false);

        // Initialize form with existing assignment data if available
        useEffect(() => {
            if (selectedRisk?.assignment && Object.keys(selectedRisk.assignment).length > 0) {
                setSelectedUsers(selectedRisk.assignment.assignedTo?.map(user => user._id) || []);
                setNotes(selectedRisk.assignment.notes || '');
                setDueDate(selectedRisk.assignment.dueDate ? moment(selectedRisk.assignment.dueDate).format('YYYY-MM-DD') : '');
                setStatus(selectedRisk.assignment.status || 'pending');
            } else {
                setSelectedUsers([]);
                setNotes('');
                setDueDate('');
                setStatus('pending');
            }
        }, [selectedRisk]);

        const handleUserToggle = (userId) => {
            setSelectedUsers(prev =>
                prev.includes(userId)
                    ? prev.filter(id => id !== userId)
                    : [...prev, userId]
            );
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (selectedUsers.length === 0) return;

            // console.log removed

            setSubmitting(true);
            try {
                await handleAssignmentSubmit({
                    assignedTo: selectedUsers,
                    notes,
                    dueDate: dueDate ? new Date(dueDate).toISOString() : null,
                    status
                });
            } finally {
                setSubmitting(false);
            }
        };

        return (
            <Modal show={showAssignmentModal} onHide={() => setShowAssignmentModal(false)} size="md">
                <Modal.Header className="bg-light border-bottom">
                    <Modal.Title className="d-flex align-items-center">
                        <i className="fa fa-edit text-primary mr-2"></i>
                        {selectedRisk?.assignment && Object.keys(selectedRisk.assignment).length > 0 ? 'Edit' : 'Create'} Risk Assignment
                    </Modal.Title>
                    <button type="button" className="close" onClick={() => setShowAssignmentModal(false)}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedRisk && (
                        <Form onSubmit={handleSubmit}>
                            {/* Risk Info Display */}
                            <div className="alert alert-info mb-4">
                                <div className="d-flex align-items-center mb-2">
                                    <i className="fa fa-shield-alt mr-2"></i>
                                    <strong>Risk Details</strong>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <small className="text-muted">Category:</small>
                                        <div className="font-weight-medium">
                                            {categories.find(c => c.value === selectedRisk.category)?.label || selectedRisk.category}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <small className="text-muted">Severity:</small>
                                        <div className="font-weight-medium">
                                            <span className={`badge badge-${severityLevels.find(s => s.value === selectedRisk.severity)?.class || 'secondary'}`}>
                                                {selectedRisk.severity}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <small className="text-muted">Title:</small>
                                    <div className="font-weight-medium">
                                        {makeUrlsClickable(selectedRisk.displayDetails?.title || 'Unknown Risk')}
                                    </div>
                                </div>
                            </div>

                            {/* User Assignment */}
                            <Form.Group className="mb-3">
                                <Form.Label className="font-weight-bold">
                                    <i className="fa fa-user mr-2"></i>
                                    Assign To *
                                </Form.Label>
                                {loadingUsers ? (
                                    <div className="text-center py-3">
                                        <div className="spinner-border spinner-border-sm text-primary mr-2" role="status">
                                            <span className="sr-only">Loading users...</span>
                                        </div>
                                        <span className="text-muted">Loading users...</span>
                                    </div>
                                ) : users.length === 0 ? (
                                    <div className="alert alert-warning">
                                        <i className="fa fa-exclamation-triangle mr-2"></i>
                                        No users available for assignment
                                    </div>
                                ) : (
                                    <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {users.map(user => (
                                            <div key={user._id} className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`user-${user._id}`}
                                                    checked={selectedUsers.includes(user._id)}
                                                    onChange={() => handleUserToggle(user._id)}
                                                />
                                                <label className="form-check-label" htmlFor={`user-${user._id}`}>
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-light rounded-circle d-flex align-items-center justify-content-center mr-2" style={{ width: '24px', height: '24px' }}>
                                                            <i className="fa fa-user text-muted" style={{ fontSize: '0.6rem' }}></i>
                                                        </div>
                                                        <div>
                                                            <div className="font-weight-medium">{user.fullName}</div>
                                                            <small className="text-muted">{user.email}</small>
                                                        </div>
                                                    </div>
                                                </label>
                                            </div>
                                        ))}
                                        {selectedUsers.length > 0 && (
                                            <div className="mt-3 p-2 bg-light rounded">
                                                <small className="text-muted">
                                                    <i className="fa fa-users mr-1"></i>
                                                    Selected: {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Form.Group>

                            {/* Status */}
                            <Form.Group className="mb-3">
                                <Form.Label className="font-weight-bold">
                                    <i className="fa fa-flag mr-2"></i>
                                    Status
                                </Form.Label>
                                <Form.Control
                                    as="select"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                >
                                    {assignmentStatuses.map(status => (
                                        <option key={status.value} value={status.value}>
                                            {status.label}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>

                            {/* Due Date */}
                            <Form.Group className="mb-3">
                                <Form.Label className="font-weight-bold">
                                    <i className="fa fa-calendar mr-2"></i>
                                    Due Date
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                />
                            </Form.Group>

                            {/* Notes */}
                            <Form.Group className="mb-3">
                                <Form.Label className="font-weight-bold">
                                    <i className="fa fa-sticky-note mr-2"></i>
                                    Notes (Optional)
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Add any notes about this assignment..."
                                />
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-light border-top">
                    <Button variant="secondary" onClick={() => setShowAssignmentModal(false)}>
                        <i className="fa fa-times mr-1"></i>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={submitting || selectedUsers.length === 0}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-save mr-1"></i>
                                {selectedRisk?.assignment && Object.keys(selectedRisk.assignment).length > 0 ? 'Update' : 'Create'} Assignment
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const RiskDetailModal = () => (
        <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" className="fade">
            <Modal.Header className="bg-light border-bottom">
                <Modal.Title className="d-flex align-items-center">
                    <i className="fa fa-shield-alt text-primary mr-2"></i>
                    {selectedRisk?.categoryDisplayName && !shouldHideValue(selectedRisk.categoryDisplayName)
                        ? makeUrlsClickable(selectedRisk.categoryDisplayName)
                        : 'Risk Details'}
                </Modal.Title>
                <button type="button" className="close" onClick={() => setShowDetailModal(false)}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </Modal.Header>
            <Modal.Body className="p-4">
                {selectedRisk && (
                    <div>
                        <div className="row">
                            {!shouldHideValue(selectedRisk.category) && (
                                <div className="col-md-6 mb-4">
                                    <div className="card border-0 bg-light">
                                        <div className="card-body">
                                            <h6 className="text-muted font-weight-bold mb-2">
                                                <i className="fa fa-tags mr-2"></i>
                                                Category
                                            </h6>
                                            <p className="mb-0 font-weight-medium">
                                                {categories.find(c => c.value === selectedRisk.category)?.label || selectedRisk.category}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!shouldHideValue(selectedRisk.severity) && (
                                <div className="col-md-6 mb-4">
                                    <div className="card border-0 bg-light">
                                        <div className="card-body">
                                            <h6 className="text-muted font-weight-bold mb-2">
                                                <i className="fa fa-exclamation-triangle mr-2"></i>
                                                Severity
                                            </h6>
                                            <span className={`badge badge-${severityLevels.find(s => s.value === selectedRisk.severity)?.class || 'secondary'} px-3 py-2`}>
                                                <i className={`fa fa-circle mr-1 text-${severityLevels.find(s => s.value === selectedRisk.severity)?.class || 'secondary'}`}></i>
                                                {selectedRisk.severity}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* {selectedRisk.displayDetails?.subtitle && renderSubtitleWithScoreCheck(selectedRisk.displayDetails.subtitle) && (
                            <div className="mb-4">
                                <h6 className="text-muted font-weight-bold mb-2">
                                    <i className="fa fa-file-text mr-2"></i>
                                    Subtitle
                                </h6>
                                <p className="mb-0">{renderSubtitleWithScoreCheck(selectedRisk.displayDetails.subtitle)}</p>
                            </div>
                        )} */}

                        {selectedRisk.displayDetails?.description && !shouldHideValue(selectedRisk.displayDetails.description) && (
                            <div className="mb-4">
                                <h6 className="text-muted font-weight-bold mb-2">
                                    <i className="fa fa-info-circle mr-2"></i>
                                    Description
                                </h6>
                                <div className="mb-0" style={{ lineHeight: '1.6' }}>
                                    {selectedRisk.displayDetails.description.split(',').map((part, index) => (
                                        <p key={index} style={{ marginBottom: index < selectedRisk.displayDetails.description.split(',').length - 1 ? '4px' : '0' }}>
                                            {makeUrlsClickable(part.trim())}
                                        </p>
                                    ))}
                                </div>
                                {selectedRisk.displayDetails?.subtitle && renderSubtitleWithScoreCheck(selectedRisk.displayDetails.subtitle) && (
                                    <div className="mb-0" style={{ lineHeight: '1.6' }}>
                                        {selectedRisk.displayDetails.subtitle.includes(',') ? (
                                            selectedRisk.displayDetails.subtitle.split(',').map((part, index) => (
                                                <p key={index} className="mb-1" style={{ marginBottom: index < selectedRisk.displayDetails.subtitle.split(',').length - 1 ? '4px' : '0' }}>
                                                    {renderSubtitleWithScoreCheck(part.trim())}
                                                </p>
                                            ))
                                        ) : (
                                            <p className="mb-0">{renderSubtitleWithScoreCheck(selectedRisk.displayDetails.subtitle)}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedRisk.displayDetails?.text && !shouldHideValue(selectedRisk.displayDetails.text) && (
                            <div className="mb-4">
                                <h6 className="text-muted font-weight-bold mb-2">
                                    <i className="fa fa-file-text mr-2"></i>
                                    Additional Information
                                </h6>
                                <p className="mb-0">{makeUrlsClickable(selectedRisk.displayDetails.text)}</p>
                            </div>
                        )}

                        {selectedRisk.displayDetails?.remediation && Array.isArray(selectedRisk.displayDetails.remediation) && selectedRisk.displayDetails.remediation.length > 0 && (
                            <div className="mb-4">
                                <h6 className="text-muted font-weight-bold mb-3">
                                    <i className="fa fa-tools mr-2"></i>
                                    Recommended Remediation Steps
                                </h6>
                                <div className="list-group">
                                    {selectedRisk.displayDetails.remediation
                                        .filter(step => !shouldHideValue(step))
                                        .map((step, index) => (
                                            <div key={index} className="list-group-item border-0 bg-light mb-2">
                                                <div className="d-flex align-items-start">
                                                    <span className="badge badge-primary mr-3 mt-1">{index + 1}</span>
                                                    <span>{makeUrlsClickable(step)}</span>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        {selectedRisk.assignment && Object.keys(selectedRisk.assignment).length > 0 && (
                            <div className="mb-4">
                                <h6 className="text-muted font-weight-bold mb-3">
                                    <i className="fa fa-user mr-2"></i>
                                    Assignment Details
                                </h6>
                                <div className="card border-0 bg-light">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <h6 className="text-muted font-weight-bold mb-2">Assigned To</h6>
                                                <div className="d-flex flex-wrap">
                                                    {selectedRisk.assignment.assignedTo?.map((user, index) => (
                                                        <span key={user._id} className="badge badge-primary mr-2 mb-1">
                                                            <i className="fa fa-user mr-1"></i>
                                                            {user.fullName}
                                                        </span>
                                                    )) || <span className="text-muted">No users assigned</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <h6 className="text-muted font-weight-bold mb-2">Status</h6>
                                                <span className={`badge badge-${assignmentStatuses.find(s => s.value === selectedRisk.assignment.status)?.class || 'secondary'} px-3 py-2`}>
                                                    {assignmentStatuses.find(s => s.value === selectedRisk.assignment.status)?.label || selectedRisk.assignment.status}
                                                </span>
                                            </div>
                                        </div>
                                        {selectedRisk.assignment.dueDate && (
                                            <div className="mb-3">
                                                <h6 className="text-muted font-weight-bold mb-2">Due Date</h6>
                                                <span className={`text-${moment(selectedRisk.assignment.dueDate).isBefore(moment()) ? 'danger' : 'muted'}`}>
                                                    <i className="fa fa-calendar mr-1"></i>
                                                    {moment(selectedRisk.assignment.dueDate).format('MMMM DD, YYYY')}
                                                    {moment(selectedRisk.assignment.dueDate).isBefore(moment()) && ' (Overdue)'}
                                                </span>
                                            </div>
                                        )}
                                        {selectedRisk.assignment.notes && (
                                            <div className="mb-3">
                                                <h6 className="text-muted font-weight-bold mb-2">Notes</h6>
                                                <p className="mb-0">{selectedRisk.assignment.notes}</p>
                                            </div>
                                        )}
                                        <div className="mb-3">
                                            <h6 className="text-muted font-weight-bold mb-2">Assigned By</h6>
                                            <span className="text-muted">
                                                <i className="fa fa-user mr-1"></i>
                                                {selectedRisk.assignment.assignedBy?.fullName || 'Unknown'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light border-top">
                <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                    <i className="fa fa-times mr-1"></i>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );

    // Action Modal Component
    const ActionModal = React.memo(() => {
        const [submitting, setSubmitting] = useState(false);
        const [localActionNote, setLocalActionNote] = useState(actionNote);
        const [localActionFor, setLocalActionFor] = useState(actionFor);

        // Sync local state with parent state when modal opens
        useEffect(() => {
            if (showActionModal) {
                setLocalActionNote(actionNote);
                setLocalActionFor(actionFor);
            }
        }, [showActionModal, actionNote, actionFor]);

        const handleSubmit = useCallback(async (e) => {
            e.preventDefault();
            if (!actionType) return;

            // Update parent state with local values before submitting
            setActionNote(localActionNote);
            setActionFor(localActionFor);

            setSubmitting(true);
            try {
                const riskIds = selectedRisk ? [selectedRisk._id] : selectedRisks;
                await handleRiskAction(riskIds, actionType, localActionNote, localActionFor);
                // Modal will be closed by handleRiskAction on success
            } catch (error) {
                console.error('Action submission error:', error);
                // Keep modal open on error so user can retry
            } finally {
                if (isMountedRef.current) {
                    setSubmitting(false);
                }
            }
        }, [actionType, selectedRisk, selectedRisks, localActionNote, localActionFor, handleRiskAction, setActionNote, setActionFor]);

        // Local onChange handlers to prevent modal flickering
        const handleActionNoteChange = useCallback((e) => {
            setLocalActionNote(e.target.value);
        }, []);

        const handleActionForChange = useCallback((e) => {
            setLocalActionFor(e.target.value);
        }, []);

        const handleActionTypeChange = useCallback((type) => {
            setActionType(type);
        }, [setActionType]);

        const getActionTitle = () => {
            switch (actionType) {
                case 'markAsRemediated': return 'Mark as Remediated';
                case 'markAsFalsePositive': return 'Mark as False Positive';
                case 'riskAccepted': return 'Accept Risk';
                case 'resetAction': return 'Reset Action';
                default: return 'Risk Action';
            }
        };

        const getActionIcon = () => {
            switch (actionType) {
                case 'markAsRemediated': return 'fa-check-circle text-success';
                case 'markAsFalsePositive': return 'fa-ban text-info';
                case 'riskAccepted': return 'fa-shield text-warning';
                case 'resetAction': return 'fa-undo text-secondary';
                default: return 'fa-flag text-primary';
            }
        };

        return (
            <Modal
                key={`action-modal-${selectedRisk?._id || 'bulk'}`}
                show={showActionModal}
                onHide={() => setShowActionModal(false)}
                size="md"
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header className="bg-light border-bottom">
                    <Modal.Title className="d-flex align-items-center">
                        <i className={`fa ${getActionIcon()} mr-2`}></i>
                        {getActionTitle()}
                    </Modal.Title>
                    <button type="button" className="close" onClick={() => setShowActionModal(false)}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                        {/* Risk Info Display */}
                        {selectedRisk && (
                            <div className="alert alert-info mb-4">
                                <div className="d-flex align-items-center mb-2">
                                    <i className="fa fa-shield-alt mr-2"></i>
                                    <strong>Risk Details</strong>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <small className="text-muted">Category:</small>
                                        <div className="font-weight-medium">
                                            {categories.find(c => c.value === selectedRisk.category)?.label || selectedRisk.category}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <small className="text-muted">Severity:</small>
                                        <div className="font-weight-medium">
                                            <span className={`badge badge-${severityLevels.find(s => s.value === selectedRisk.severity)?.class || 'secondary'}`}>
                                                {selectedRisk.severity}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <small className="text-muted">Title:</small>
                                    <div className="font-weight-medium">
                                        {makeUrlsClickable(selectedRisk.displayDetails?.title || 'Unknown Risk')}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Bulk Action Info */}
                        {!selectedRisk && selectedRisks.length > 0 && (
                            <div className="alert alert-warning mb-4">
                                <div className="d-flex align-items-center mb-2">
                                    <i className="fa fa-users mr-2"></i>
                                    <strong>Bulk Action</strong>
                                </div>
                                <p className="mb-0">
                                    This action will be applied to <strong>{selectedRisks.length}</strong> selected risk{selectedRisks.length !== 1 ? 's' : ''}.
                                </p>
                            </div>
                        )}

                        {/* Action Type Selection for Bulk Actions */}
                        {!selectedRisk && selectedRisks.length > 0 && (
                            <Form.Group className="mb-3">
                                <Form.Label className="font-weight-bold">
                                    <i className="fa fa-flag mr-2"></i>
                                    Action Type *
                                </Form.Label>
                                <div className="row">
                                    <div className="col-md-4 mb-2">
                                        <div
                                            className={`card border-2 ${actionType === 'markAsFalsePositive' ? 'border-info bg-info-light' : 'border-light'}`}
                                            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                            onClick={() => handleActionTypeChange('markAsFalsePositive')}
                                        >
                                            <div className="card-body text-center p-3">
                                                <i className="fa fa-ban text-info mb-2" style={{ fontSize: '1.5rem' }}></i>
                                                <h6 className="mb-1 font-weight-bold">False Positive</h6>
                                                <small className="text-muted">Mark as false positive</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-2">
                                        <div
                                            className={`card border-2 ${actionType === 'markAsRemediated' ? 'border-success bg-success-light' : 'border-light'}`}
                                            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                            onClick={() => handleActionTypeChange('markAsRemediated')}
                                        >
                                            <div className="card-body text-center p-3">
                                                <i className="fa fa-check-circle text-success mb-2" style={{ fontSize: '1.5rem' }}></i>
                                                <h6 className="mb-1 font-weight-bold">Risk Remediated</h6>
                                                <small className="text-muted">Mark as remediated</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-2">
                                        <div
                                            className={`card border-2 ${actionType === 'riskAccepted' ? 'border-warning bg-warning-light' : 'border-light'}`}
                                            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                            onClick={() => handleActionTypeChange('riskAccepted')}
                                        >
                                            <div className="card-body text-center p-3">
                                                <i className="fa fa-shield text-warning mb-2" style={{ fontSize: '1.5rem' }}></i>
                                                <h6 className="mb-1 font-weight-bold">Risk Accepted</h6>
                                                <small className="text-muted">Accept the risk</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Form.Group>
                        )}

                        {/* Action Duration */}
                        <Form.Group className="mb-3">
                            <Form.Label className="font-weight-bold">
                                <i className="fa fa-clock mr-2"></i>
                                Action Duration
                            </Form.Label>
                            <Form.Control
                                as="select"
                                value={localActionFor}
                                onChange={handleActionForChange}
                            >
                                <option value="forever">Forever</option>
                                <option value="3 months">3 Months</option>
                                <option value="6 months">6 Months</option>
                                <option value="1 year">1 Year</option>
                            </Form.Control>
                        </Form.Group>

                        {/* Action Note */}
                        <Form.Group className="mb-3">
                            <Form.Label className="font-weight-bold">
                                <i className="fa fa-sticky-note mr-2"></i>
                                Action Note (Optional)
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={localActionNote}
                                onChange={handleActionNoteChange}
                                placeholder="Add any notes about this action..."
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="bg-light border-top">
                    <Button variant="secondary" onClick={() => setShowActionModal(false)}>
                        <i className="fa fa-times mr-1"></i>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={submitting || (!selectedRisk && !actionType)}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                                Processing...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-save mr-1"></i>
                                {!selectedRisk && !actionType ? 'Select Action Type' : getActionTitle()}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    });

    const FreshdeskModal = () => (
        <Modal show={showFreshdeskModal} onHide={closeFreshdeskModal} size="md">
            <Modal.Header className="bg-light border-bottom">
                <Modal.Title className="d-flex align-items-center">
                    <i className="fa fa-ticket text-info mr-2"></i>
                    Create Freshdesk Ticket
                </Modal.Title>
                <button type="button" className="close" onClick={closeFreshdeskModal}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </Modal.Header>
            <Modal.Body className="p-4">
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            value={freshdeskForm.email}
                            onChange={(e) => handleFreshdeskFieldChange('email', e.target.value)}
                            placeholder="Requester email"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                            type="text"
                            value={freshdeskForm.title}
                            onChange={(e) => handleFreshdeskFieldChange('title', e.target.value)}
                            placeholder="Ticket title"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={freshdeskForm.description}
                            onChange={(e) => handleFreshdeskFieldChange('description', e.target.value)}
                            placeholder="Describe the issue or context"
                        />
                    </Form.Group>
                    <div className="form-row">
                        <Form.Group className="mb-3 col-md-4">
                            <Form.Label>Priority</Form.Label>
                            <Form.Control
                                as="select"
                                value={freshdeskForm.priority}
                                onChange={(e) => handleFreshdeskFieldChange('priority', e.target.value)}
                            >
                                <option value={1}>Low</option>
                                <option value={2}>Medium</option>
                                <option value={3}>High</option>
                                <option value={4}>Urgent</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="mb-3 col-md-4">
                            <Form.Label>Status</Form.Label>
                            <Form.Control
                                as="select"
                                value={freshdeskForm.status}
                                onChange={(e) => handleFreshdeskFieldChange('status', e.target.value)}
                            >
                                <option value={2}>Open</option>
                                <option value={3}>Pending</option>
                                <option value={4}>Resolved</option>
                                <option value={5}>Closed</option>
                            </Form.Control>
                        </Form.Group>

                    </div>
                    <Form.Group className="mb-3">
                        <Form.Label>Tags (comma separated)</Form.Label>
                        <Form.Control
                            type="text"
                            value={Array.isArray(freshdeskForm.tags) ? freshdeskForm.tags.join(', ') : freshdeskForm.tags}
                            onChange={(e) => handleFreshdeskFieldChange('tags', e.target.value)}
                            placeholder="e.g. tag1, tag2"
                        />
                        <small className="text-muted">Tags are optional.</small>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className="bg-light border-top">
                <Button variant="secondary" onClick={closeFreshdeskModal} disabled={submittingFreshdesk}>
                    <i className="fa fa-times mr-1"></i>
                    Cancel
                </Button>
                <Button variant="primary" onClick={submitFreshdeskTicket} disabled={submittingFreshdesk}>
                    {submittingFreshdesk ? (
                        <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Creating...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-save mr-1"></i>
                            Create Ticket
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    // Report generation functions
    const downloadFileExecutive = async () => {
        try {
            const session = localStorage.getItem("session");
            axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;

            let clientName = "";
            if (JSON.parse(localStorage.getItem("OrgName"))) {
                if (sessionStorage.getItem('vendorId')) {
                    clientName = sessionStorage.getItem('vendorName')
                } else {
                    clientName = JSON.parse(localStorage.getItem("OrgName"));
                }
            }

            const response = await axios.get(
                `${API_URL}/pptxreport/${id}`,
                {
                    responseType: "blob",
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${clientName?.replace(/\s+/g, '-')}ExecutiveReport.pptx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // console.error removed
            toastr.error('Error', 'Failed to download executive report');
        }
    };

    const downloadFullReport = async () => {
        try {
            const session = localStorage.getItem("session");
            axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;

            let clientName = "";
            if (JSON.parse(localStorage.getItem("OrgName"))) {
                if (sessionStorage.getItem('vendorId')) {
                    clientName = sessionStorage.getItem('vendorName')
                } else {
                    clientName = JSON.parse(localStorage.getItem("OrgName"));
                }
            }

            const response = await axios.get(
                `${API_URL}/downloadSummaryReport/${id}?reportType=Full`,
                {
                    responseType: "blob",
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${clientName?.replace(/\s+/g, '-')}FullReport.docx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // console.error removed
            toastr.error('Error', 'Failed to download full report');
        }
    };

    const downloadExecutiveWordReport = async () => {
        try {
            const session = localStorage.getItem("session");
            axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;

            let clientName = "";
            if (JSON.parse(localStorage.getItem("OrgName"))) {
                if (sessionStorage.getItem('vendorId')) {
                    clientName = sessionStorage.getItem('vendorName')
                } else {
                    clientName = JSON.parse(localStorage.getItem("OrgName"));
                }
            }

            const response = await axios.get(
                `${API_URL}/downloadSummaryReport/${id}?reportType=executiveWordReport`,
                {
                    responseType: "blob",
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${clientName?.replace(/\s+/g, '-')}ExecutiveWordReport.docx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            // console.error removed
            toastr.error('Error', 'Failed to download executive word report');
        }
    };

    const navigateToCustomReport = () => {
        // Navigate to custom report page with the necessary parameters
        window.open(`/custom-report/${id}`, '_blank');
    };

    const downloadSummaryReport = async () => {
        try {
            const session = localStorage.getItem("session");
            axios.defaults.headers.common["Authorization"] = `Bearer ${session}`;

            let clientName = "";
            if (JSON.parse(localStorage.getItem("OrgName"))) {
                if (sessionStorage.getItem('vendorId')) {
                    clientName = sessionStorage.getItem('vendorName')
                } else {
                    clientName = JSON.parse(localStorage.getItem("OrgName"));
                }
            }

            const response = await axios.get(
                `${API_URL}/organisations/${id}/summary-report`,
                {
                    responseType: "blob",
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${clientName?.replace(/\s+/g, '-')}-Summary-Report.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading summary report:', error);
            toastr.error('Error', 'Failed to download summary report');
        }
    };

    const generateReport = (v) => {
        if (v == "Full") {
            if (loading) {
                return false;
            }
            downloadFullReport()
        }
        if (v == "Executive") {
            downloadFileExecutive();
        }
        if (v == "ExecutiveWord") {
            downloadExecutiveWordReport();
        }
        if (v == "Summary") {
            downloadSummaryReport();
        }
        if (v == "Custom") {
            navigateToCustomReport();
        }
    };

    // Show loading state when id is not available
    if (!id) {
        return (
            <div className="container-fluid">
                <div className="card shadow-sm border-0">
                    <div className="card-body text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="sr-only">Loading organization data...</span>
                        </div>
                        <h5 className="mt-4 mb-2 text-muted">Loading Organization Data</h5>
                        <p className="text-muted mb-0">Please wait while we fetch your security risks...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            {/* Header Section */}
            <div className="row mb-4 mt-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="mb-1 text-dark font-weight-bold">
                                <i className="fa fa-shield-alt text-primary mr-2"></i>
                                Consolidated Risks
                            </h2>
                            <p className="text-muted mb-0">Manage and track all security risks across your organization</p>
                        </div>
                        <div className="d-flex align-items-center">
                            <Box sx={{ minWidth: 200 }}>
                                <FormControl fullWidth>
                                    <MuiSelect
                                        displayEmpty
                                        renderValue={(value) =>
                                            value === "" ? <em>Download Reports</em> : value
                                        }
                                        value={selectedValue}
                                        onChange={(event) => {
                                            generateReport(event.target.value);
                                            setSelectedValue(''); // Reset after selection
                                        }}
                                        style={{
                                            color: "white",
                                            fontWeight: "bold",
                                            backgroundColor: "#453acd",
                                            borderRadius: "6px"
                                        }}
                                        sx={{
                                            '.MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#453acd',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#453acd',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#453acd',
                                            },
                                            '.MuiSelect-icon': {
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        <MenuItem value="Full">Full Report</MenuItem>
                                        <MenuItem value="Summary">Summary Report</MenuItem>
                                        <MenuItem value="Executive">Executive Report</MenuItem>
                                        <MenuItem value="ExecutiveWord">Executive Word Report</MenuItem>
                                        <MenuItem value="Custom">Create Custom Report</MenuItem>
                                    </MuiSelect>
                                </FormControl>
                            </Box>
                        </div>
                    </div>
                </div>
            </div>

            {viewMode === 'list' ? (
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-white border-bottom">
                        <div className="row align-items-center">
                            <div className="col-md-8">
                                <h4 className="mb-0 text-dark font-weight-bold">
                                    <i className="fa fa-filter text-primary mr-2"></i>
                                    Risk Management Dashboard
                                </h4>
                            </div>
                            <div className="col-md-4 text-right">
                                <span className="badge badge-light text-muted">
                                    <i className="fa fa-info-circle mr-1"></i>
                                    {pagination.totalRecords} Total Risks
                                </span>
                            </div>
                        </div>

                        {/* Enhanced Filters Section */}
                        <div className="row mt-4">
                            <div className="col-lg-2 col-md-6 mb-3">
                                <label className="form-label text-muted font-weight-bold small">
                                    <i className="fa fa-exclamation-triangle mr-1"></i>
                                    Severity Filter
                                </label>
                                <select
                                    className="form-control form-control-sm border-0 bg-light"
                                    value={filters.severity || ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        handleFilterChange('severity', value);
                                        // Note: No need to call fetchRisks directly here
                                        // The filters change will trigger the useEffect that fetches data
                                    }}
                                >
                                    <option value="">All Severities</option>
                                    {severityLevels.map(level => (
                                        <option key={level.value} value={level.value}>
                                            {level.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <label className="form-label text-muted font-weight-bold small">
                                    <i className="fa fa-search mr-1"></i>
                                    Search Risks
                                </label>
                                <div className="input-group input-group-sm">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm border-0 bg-light"
                                        placeholder="Search risks..."
                                        value={searchInput}
                                        onChange={(e) => {
                                            setSearchInput(e.target.value);
                                            // Clear selected records when search changes
                                            setSelectedRisks([]);
                                            // Debounce search to avoid too many API calls
                                            if (filtersFetchTimerRef.current) {
                                                clearTimeout(filtersFetchTimerRef.current);
                                            }
                                            filtersFetchTimerRef.current = setTimeout(() => {
                                                setSearch(e.target.value);
                                            }, 500);
                                        }}
                                        style={{
                                            minHeight: '38px',
                                            padding: '10px 12px'
                                        }}
                                    />
                                    {searchInput && (
                                        <div className="input-group-append">
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={() => {
                                                    setSearchInput("");
                                                    setSearch("");
                                                }}
                                                style={{
                                                    minHeight: '38px',
                                                    borderLeft: 'none'
                                                }}
                                            >
                                                <i className="fa fa-times"></i>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-6 mb-3">
                                <label className="form-label text-muted font-weight-bold small">
                                    <i className="fa fa-tags mr-1"></i>
                                    Category Filter
                                </label>
                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="outline-light"
                                        className="form-control form-control-sm border-0 bg-light text-left d-flex justify-content-between align-items-center"
                                        style={{
                                            minHeight: '38px',
                                            padding: '10px 10px',
                                            position: 'relative'
                                        }}
                                    >
                                        <span className="text-truncate">
                                            {filters.category.length === 0
                                                ? 'All Categories'
                                                : filters.category.length === 1
                                                    ? categories.find(c => c.value === filters.category[0])?.label || 'Selected'
                                                    : `${filters.category.length} Categories Selected`
                                            }
                                        </span>
                                        <i className="fa fa-chevron-down text-muted ml-auto" style={{ display: 'block' }}></i>
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="shadow-lg border-0" style={{
                                        minWidth: '350px',
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                        marginTop: '0',
                                        borderTopLeftRadius: '0',
                                        borderTopRightRadius: '0',
                                        borderTop: '1px solid #e9ecef'
                                    }}>
                                        <div className="px-4 py-3 border-bottom bg-light">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center">
                                                    <i className="fa fa-tags text-primary mr-2"></i>
                                                    <small className="text-muted font-weight-bold">Select Categories</small>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm px-2 py-1"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleClearAllCategories();
                                                    }}
                                                    title="Clear all categories"
                                                    style={{ fontSize: '0.75rem', marginLeft: '1rem' }}
                                                >
                                                    <i className="fa fa-times mr-1"></i>
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                        <div className="p-3">
                                            {categories.map(category => (
                                                <div key={category.value} className="form-check mb-2">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`category-${category.value}`}
                                                        checked={filters.category.includes(category.value)}
                                                        onChange={(e) => handleCategoryFilterChange(category.value, e.target.checked)}
                                                    />
                                                    <label className="form-check-label small mb-0 d-flex align-items-center" htmlFor={`category-${category.value}`}>
                                                        <span className="mr-2">{category.label}</span>
                                                        {filters.category.includes(category.value) && (
                                                            <i className="fa fa-check text-primary ml-auto" style={{ fontSize: '0.7rem' }}></i>
                                                        )}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>
                                {filters.category.length > 0 && (
                                    <div className="mt-2">
                                        <small className="text-muted d-block mb-1">
                                            <i className="fa fa-info-circle mr-1"></i>
                                            {filters.category.length} category{filters.category.length !== 1 ? 'ies' : 'y'} selected:
                                        </small>
                                        <div className="d-flex flex-wrap">
                                            {filters.category.map(catValue => {
                                                const category = categories.find(c => c.value === catValue);
                                                return (
                                                    <span key={catValue} className="badge badge-primary badge-sm mr-1 mb-1" style={{ padding: '5px 3px 4px 4px' }}>
                                                        {category?.label || catValue}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="col-lg-2 col-md-6 mb-3">
                                <label className="form-label text-muted font-weight-bold small">
                                    <i className="fa fa-users mr-1"></i>
                                    Assigned To
                                </label>
                                <select
                                    className="form-control form-control-sm border-0 bg-light"
                                    value={filters.assignedTo.length > 0 ? filters.assignedTo[0] : ''}
                                    onChange={(e) => handleFilterChange('assignedTo', e.target.value ? [e.target.value] : [])}
                                >
                                    <option value="">All Assignees</option>
                                    <option value="unassigned">Unassigned</option>
                                    {users.map(user => (
                                        <option key={user._id} value={user._id}>
                                            {user.fullName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-lg-2 col-md-6 mb-3">
                                <label className="form-label text-muted font-weight-bold small">
                                    <i className="fa fa-flag mr-1"></i>
                                    Assignment Status
                                </label>
                                <select
                                    className="form-control form-control-sm border-0 bg-light"
                                    value={filters.assignmentStatus}
                                    onChange={(e) => handleFilterChange('assignmentStatus', e.target.value)}
                                >
                                    <option value="">All Status</option>
                                    {assignmentStatuses.map(status => (
                                        <option key={status.value} value={status.value}>
                                            {status.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-lg-2 col-md-6 mb-3">
                                <label className="form-label text-muted font-weight-bold small">
                                    <i className="fa fa-calendar mr-1"></i>
                                    From Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm border-0 bg-light"
                                    value={filters.from}
                                    onChange={(e) => handleFilterChange('from', e.target.value)}
                                />
                            </div>
                            <div className="col-lg-2 col-md-6 mb-3">
                                <label className="form-label text-muted font-weight-bold small">
                                    <i className="fa fa-calendar mr-1"></i>
                                    To Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm border-0 bg-light"
                                    value={filters.to}
                                    onChange={(e) => handleFilterChange('to', e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Filter Summary */}
                        {(filters.severity || filters.category.length > 0 || filters.assignedTo.length > 0 || filters.assignmentStatus || filters.from || filters.to) && (
                            <div className="row mt-2">
                                <div className="col-12">
                                    <div className="d-flex align-items-center p-2 bg-light rounded">
                                        <small className="text-muted font-weight-bold mr-2">
                                            <i className="fa fa-filter mr-1"></i>
                                            Active Filters:
                                        </small>
                                        <div className="d-flex flex-wrap">
                                            {filters.severity && (
                                                <span className="badge badge-info badge-sm mr-1 mb-1">
                                                    Severity: {filters.severity}
                                                </span>
                                            )}
                                            {filters.category.length > 0 && (
                                                <span className="badge badge-primary badge-sm mr-1 mb-1">
                                                    Categories: {filters.category.length} selected
                                                </span>
                                            )}
                                            {filters.assignedTo.length > 0 && (
                                                <span className="badge badge-warning badge-sm mr-1 mb-1">
                                                    Assigned: {filters.assignedTo.length} selected
                                                </span>
                                            )}
                                            {filters.assignmentStatus && (
                                                <span className="badge badge-secondary badge-sm mr-1 mb-1">
                                                    Status: {filters.assignmentStatus}
                                                </span>
                                            )}
                                            {filters.from && (
                                                <span className="badge badge-success badge-sm mr-1 mb-1">
                                                    From: {filters.from}
                                                </span>
                                            )}
                                            {filters.to && (
                                                <span className="badge badge-success badge-sm mr-1 mb-1">
                                                    To: {filters.to}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Additional Controls */}
                        <div className="row mt-2">
                            <div className="col-lg-3 col-md-6 mb-3">
                                <label className="form-label text-muted font-weight-bold small">
                                    <i className="fa fa-list-ol mr-1"></i>
                                    Items per Page
                                </label>
                                <select
                                    className="form-control form-control-sm border-0 bg-light"
                                    value={pagination.limit}
                                    onChange={(e) => {
                                        if (isMountedRef.current) {
                                            const newLimit = Number(e.target.value);

                                            // Clear any existing filter timers that might interfere
                                            if (filtersFetchTimerRef.current) {
                                                clearTimeout(filtersFetchTimerRef.current);
                                            }

                                            setPagination(prev => ({ ...prev, limit: newLimit, currentPage: 1 }));

                                            // Fetch risks with the new limit immediately to prevent other useEffects from interfering
                                            fetchRisks(1, newLimit);
                                        }
                                    }}
                                >
                                    <option value={20}>20 items</option>
                                    <option value={50}>50 items</option>
                                    <option value={100}>100 items</option>
                                </select>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3 d-flex align-items-center">
                                <button
                                    className="btn btn-outline-secondary btn-sm w-100"
                                    onClick={handleClearAllFilters}
                                >
                                    <i className="fa fa-refresh mr-1"></i>
                                    Clear All Filters
                                </button>
                            </div>
                            <div className="col-lg-6 col-md-12 mb-3 d-flex align-items-end">
                                <div className="d-flex w-100 flex-wrap">
                                    <div className="d-flex me-2 mb-2">
                                        <button
                                            className="btn btn-outline-primary btn-sm mr-2"
                                            onClick={handleBulkAction}
                                            disabled={selectedRisks.length === 0}
                                        >
                                            <i className="fa fa-flag mr-1"></i>
                                            Bulk Actions ({selectedRisks.length})
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm mr-2"
                                            onClick={handleSelectAll}
                                        >
                                            <i className="fa fa-check-square mr-1"></i>
                                            {selectedRisks.length === risks.length ? 'Deselect All' : 'Select All'}
                                        </button>
                                    </div>
                                    <div className="d-flex">
                                        <button
                                            className="btn btn-primary btn-sm mr-2"
                                            onClick={() => downloadConsolidatedReport(false)}
                                            disabled={downloadingReport}
                                        >
                                            {downloadingReport ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                    Downloading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-download mr-1"></i>
                                                    Download All Risks
                                                </>
                                            )}
                                        </button>
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={() => downloadConsolidatedReport(true)}
                                            disabled={downloadingReport || selectedRisks.length === 0}
                                            title={selectedRisks.length === 0 ? "Select risks first" : `Download ${selectedRisks.length} selected risks`}
                                        >
                                            <i className="fa fa-file-export mr-1"></i>
                                            Download Selected ({selectedRisks.length})
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <ul className="nav nav-tabs mb-2" id="myTabs" role="tablist">
                        <li className="nav-item">
                            <a
                                className={`nav-table-tab nav-link ${categoryV === "all" ? "active" : ""
                                    }`}
                                onClick={() => {
                                    if (!isMountedRef.current) return;
                                    setCategoryV("all");
                                    setSelectedIds([]);
                                    setSearch("");
                                    setSearchInput("");
                                    setFilters(prev => ({
                                        ...prev,
                                        severity: '',
                                        minExploitabilityScore: '',
                                        minExploitabilityScoreInput: ''
                                    }));
                                }}
                            >
                                Current
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={`nav-link nav-table-tab ${categoryV === "risk-accepted" ? "active" : ""
                                    }`}
                                onClick={() => {
                                    if (!isMountedRef.current) return;
                                    setCategoryV("risk-accepted");
                                    setSelectedIds([]);
                                    setSearch("");
                                    setSearchInput("");
                                    setFilters(prev => ({
                                        ...prev,
                                        severity: '',
                                        minExploitabilityScore: '',
                                        minExploitabilityScoreInput: ''
                                    }));
                                }}
                            >
                                Risk Accepted
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={`nav-link nav-table-tab ${categoryV === "false-positive" ? "active" : ""
                                    }`}
                                onClick={() => {
                                    if (!isMountedRef.current) return;
                                    setCategoryV("false-positive");
                                    setSelectedIds([]);
                                    setSearch("");
                                    setSearchInput("");
                                    setFilters(prev => ({
                                        ...prev,
                                        severity: '',
                                        minExploitabilityScore: '',
                                        minExploitabilityScoreInput: ''
                                    }));
                                }}
                            >
                                False Positive
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={`nav-link nav-table-tab ${categoryV === "remidiated" ? "active" : ""
                                    }`}
                                onClick={() => {
                                    if (!isMountedRef.current) return;
                                    setCategoryV("remidiated");
                                    setSelectedIds([]);
                                    setSearch("");
                                    setSearchInput("");
                                    setFilters(prev => ({
                                        ...prev,
                                        severity: '',
                                        minExploitabilityScore: '',
                                        minExploitabilityScoreInput: ''
                                    }));
                                }}
                            >
                                Remediated
                            </a>
                        </li>
                    </ul>
                    <div className="card-body p-0">
                        <div className="table-responsive" style={{ maxWidth: '100%', overflowX: 'auto' }}>
                            <table className="table table-hover mb-0" style={{ minWidth: '800px', maxWidth: '100%' }}>
                                <thead className="bg-light">
                                    <tr>
                                        <th className="border-0 py-3 px-4" style={{ width: '50px', minWidth: '50px' }}>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={selectedRisks.length === risks.length && risks.length > 0}
                                                    onChange={handleSelectAll}
                                                />
                                            </div>
                                        </th>
                                        <th className="border-0 py-3 px-4" onClick={() => handleSortChange('category')} style={{ cursor: 'pointer', width: '15%', minWidth: '120px' }}>
                                            <div className="d-flex align-items-center">
                                                <i className="fa fa-tags text-muted mr-2"></i>
                                                Category {renderSortIcon('category')}
                                            </div>
                                        </th>
                                        <th className="border-0 py-3 px-4" onClick={() => handleSortChange('severity')} style={{ cursor: 'pointer', width: '12%', minWidth: '100px' }}>
                                            <div className="d-flex align-items-center">
                                                <i className="fa fa-exclamation-triangle text-muted mr-2"></i>
                                                Severity {renderSortIcon('severity')}
                                            </div>
                                        </th>
                                        <th className="border-0 py-3 px-4" style={{ width: '30%', minWidth: '250px', maxWidth: '350px' }}>
                                            <div className="d-flex align-items-center">
                                                <i className="fa fa-file-text text-muted mr-2"></i>
                                                Title
                                            </div>
                                        </th>
                                        <th className="border-0 py-3 px-4" onClick={() => handleSortChange('createdAt')} style={{ cursor: 'pointer', width: '12%', minWidth: '100px' }}>
                                            <div className="d-flex align-items-center">
                                                <i className="fa fa-clock-o text-muted mr-2"></i>
                                                Detected At {renderSortIcon('createdAt')}
                                            </div>
                                        </th>
                                        <th className="border-0 py-3 px-4" style={{ width: '15%', minWidth: '120px' }}>
                                            <div className="d-flex align-items-center">
                                                <i className="fa fa-user text-muted mr-2"></i>
                                                Assignment
                                            </div>
                                        </th>
                                        <th className="border-0 py-3 px-4" style={{ width: '21%', minWidth: '150px' }}>
                                            <div className="d-flex align-items-center">
                                                <i className="fa fa-cogs text-muted mr-2"></i>
                                                Actions
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5">
                                                <div className="d-flex flex-column align-items-center">
                                                    <div className="spinner-border text-primary mb-3" role="status">
                                                        <span className="sr-only">Loading...</span>
                                                    </div>
                                                    <p className="text-muted mb-0">Loading risks...</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : risks.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5">
                                                <div className="d-flex flex-column align-items-center">
                                                    <i className="fa fa-inbox text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                                                    <h5 className="text-muted mb-2">No Risks Found</h5>
                                                    <p className="text-muted mb-0">Try adjusting your filters or check back later.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        risks.map(risk => renderTableRow(risk))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="card-footer bg-white border-top">
                        <div className="row align-items-center">
                            <div className="col-sm-12 col-md-6">
                                <div className="d-flex align-items-center">
                                    <span className="text-muted mr-3">
                                        <i className="fa fa-info-circle mr-1"></i>
                                        Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalRecords)} of {pagination.totalRecords} risks
                                    </span>
                                </div>
                            </div>
                            <div className="col-sm-12 col-md-6">
                                <div className="d-flex justify-content-end align-items-center">
                                    <Pagination
                                        className="mb-0"
                                        currentPage={pagination.currentPage}
                                        totalCount={pagination.totalRecords}
                                        pageSize={pagination.limit}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <RiskKanbanView
                    orgId={id}
                    categories={categories}
                    severityLevels={severityLevels}
                />
            )}
            <RiskDetailModal />
            <AssignmentModal />
            <ActionModal />
            <FreshdeskModal />
        </div>
    );
};

export default ConsolidatedRisks;
